/* eslint-disable array-callback-return */
import { Backdrop, Box, Button, CircularProgress, TextField } from '@mui/material';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import * as yup from 'yup';
import { greenColor } from '../../config/colors';
import { useCookiesLogin } from '../../config/cookies';
import { HOST_API } from '../../config/hosts';
import { useTranslation } from '../../hooks/useTranslation';

const RegisterBusiness = () => {
  const { cookies } = useCookiesLogin();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const initialValues = {
    nome: '',
    razaoSocial: '',
    cnpj: '',
    telefone: '',
    email: '',
    cnaePrincipal: '',
  };

  const validationSchema = yup.object().shape({
    nome: yup.string().required('Nome é obrigatório'),
    razaoSocial: yup.string().required('Razão Social é obrigatória'),
    cnpj: yup.string().required('CNPJ é obrigatório').min(14, 'CNPJ inválido'),
    telefone: yup.string().required('Telefone é obrigatório'),
    email: yup.string().email('Email inválido').required('Email é obrigatório'),
    cnaePrincipal: yup.string().required('CNAE Principal é obrigatório'),
  });

  const handleSubmitForm = async (values, { setSubmitting }) => {
    setIsLoading(true);
    setSubmitting(true);

    const requestBody = {
      nome: values.nome,
      razaoSocial: values.razaoSocial,
      cnpj: values.cnpj,
      telefone: values.telefone,
      email: values.email,
      cnaePrincipal: values.cnaePrincipal,
      client_id: cookies.AUTH_TOKEN.id,
    };

    try {
      const response = await fetch(`${HOST_API}/v1/business/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t('messages.businessRegistered'), { theme: 'dark' });
      } else {
        toast.error(t('messages.businessRegisterError'), {
          theme: 'dark',
        });
      }
    } catch (error) {
      toast.error(t('messages.networkError'), { theme: 'dark' });
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Box p={5}>
      <Box mt={1}>
        <ToastContainer />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmitForm}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Box display="grid" gap="30px" gridTemplateColumns="repeat(4, minmax(0, 1fr))">
                <TextField
                  fullWidth
                  variant="filled"
                  label={t('forms.name')}
                  color="secondary"
                  value={values.nome}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="nome"
                  error={!!touched.nome && !!errors.nome}
                  helperText={touched.nome && errors.nome}
                  sx={{ gridColumn: 'span 2' }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label={t('forms.companyName')}
                  color="secondary"
                  value={values.razaoSocial}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="razaoSocial"
                  error={!!touched.razaoSocial && !!errors.razaoSocial}
                  helperText={touched.razaoSocial && errors.razaoSocial}
                  sx={{ gridColumn: 'span 2' }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label={t('forms.cnpj')}
                  color="secondary"
                  value={values.cnpj}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="cnpj"
                  error={!!touched.cnpj && !!errors.cnpj}
                  helperText={touched.cnpj && errors.cnpj}
                  sx={{ gridColumn: 'span 2' }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label={t('forms.phone')}
                  color="secondary"
                  value={values.telefone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="telefone"
                  error={!!touched.telefone && !!errors.telefone}
                  helperText={touched.telefone && errors.telefone}
                  sx={{ gridColumn: 'span 2' }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label={t('forms.email')}
                  color="secondary"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="email"
                  error={!!touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  sx={{ gridColumn: 'span 2' }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label={t('forms.mainCnae')}
                  color="secondary"
                  value={values.cnaePrincipal}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="cnaePrincipal"
                  error={!!touched.cnaePrincipal && !!errors.cnaePrincipal}
                  helperText={touched.cnaePrincipal && errors.cnaePrincipal}
                  sx={{ gridColumn: 'span 2' }}
                />

                <Box sx={{ gridColumn: 'span 2' }}>
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={isLoading || !values.nome || !!Object.keys(errors).length}
                  >
                    {t('buttons.registerDealer')}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Formik>

        <Backdrop open={isLoading} style={{ zIndex: 9999 }}>
          <CircularProgress color="inherit" sx={{ color: greenColor }} />
        </Backdrop>
      </Box>
    </Box>
  );
};

export default RegisterBusiness;
